import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  classifyAiImpactAssessmentScope,
  getAssessmentReportPresignedUrl,
  rerunAssessment,
  rerunSystemAssessment,
} from "../../services/assessmentService";
import { useSnackbar } from "../../contexts/SnackbarContext";
import type { AiImpactAssessmentActionHandlers, AiImpactAssessmentRow } from "./assessmentCenterColumns";

export type UseAiImpactAssessmentRowActionsOptions = Readonly<{
  canRerunSystem?: boolean;
  onRetrySuccess?: (row: AiImpactAssessmentRow) => void;
}>;

/**
 * View / download / re-run handlers shared by Assessment Center AI table and AI Results modal.
 */
export function useAiImpactAssessmentRowActions(
  options: UseAiImpactAssessmentRowActionsOptions = {},
) {
  const { canRerunSystem = false, onRetrySuccess } = options;
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [downloadingFilePath, setDownloadingFilePath] = useState<string | null>(
    null,
  );
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState("");

  const handlePreviewPDF = useCallback(async (filePath: string) => {
    try {
      const { presigned_url } = await getAssessmentReportPresignedUrl(filePath);
      setSelectedPdfUrl(presigned_url);
      setPdfDialogOpen(true);
    } catch (error) {
      console.error("Error previewing PDF:", error);
    }
  }, []);

  const handleDownloadPDF = useCallback(
    async (filePath: string) => {
      if (busyAction) return;
      setDownloadingFilePath(filePath);
      setBusyAction(`download:${filePath}`);
      try {
        const { presigned_url, filename: backendFilename } =
          await getAssessmentReportPresignedUrl(filePath);

        let fileName = backendFilename || "assessment-report.pdf";
        if (!backendFilename) {
          try {
            const urlObj = new URL(presigned_url);
            const disposition =
              urlObj.searchParams.get("response-content-disposition") || "";
            const filenameMatch = /filename="([^"]+)"/.exec(disposition);
            if (filenameMatch?.[1]) {
              fileName = filenameMatch[1];
            } else {
              const filenameStar = /filename\*=UTF-8''(.+)/.exec(disposition);
              if (filenameStar?.[1]) {
                fileName = decodeURIComponent(filenameStar[1]);
              }
            }
          } catch (e) {
            console.error("Failed to parse filename from presigned URL", e);
          }
        }

        const xhr = new XMLHttpRequest();
        xhr.open("GET", presigned_url, true);
        xhr.responseType = "blob";
        xhr.onload = () => {
          const blobUrl = URL.createObjectURL(xhr.response);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
            setBusyAction(null);
            setDownloadingFilePath(null);
          }, 100);
        };
        xhr.onerror = () => {
          console.error("Error downloading PDF via XHR");
          setBusyAction(null);
          setDownloadingFilePath(null);
        };
        xhr.send();
      } catch (error) {
        console.error("Error downloading PDF:", error);
        setBusyAction(null);
        setDownloadingFilePath(null);
      }
    },
    [busyAction],
  );

  const handleRetry = useCallback(
    async (row: AiImpactAssessmentRow) => {
      if (busyAction || !row.taskUuid) return;
      setBusyAction(`retry:${row.taskUuid}`);
      const scopeKind = classifyAiImpactAssessmentScope(row.assessmentType);
      try {
        const payload = {
          task_uuid: row.taskUuid,
          regulation_id: row.regulationId,
          regulation_version: row.version,
        };
        const res =
          scopeKind === "system"
            ? await rerunSystemAssessment(payload)
            : await rerunAssessment(payload);
        if (res?.message) {
          showMessage(res.message, "success");
        } else {
          showMessage("Re-run started.", "success");
        }
        await queryClient.invalidateQueries({ queryKey: ["assessment-center"] });
        onRetrySuccess?.(row);
      } catch (error) {
        console.error("Error rerunning assessment:", error);
        showMessage(
          error instanceof Error ? error.message : "Failed to re-run assessment",
          "error",
        );
      } finally {
        setBusyAction(null);
      }
    },
    [busyAction, onRetrySuccess, queryClient, showMessage],
  );

  const actionHandlers: AiImpactAssessmentActionHandlers = useMemo(
    () => ({
      onViewReport: (row) => {
        const filePath = row.reportUrl;
        if (!filePath) return;
        void handlePreviewPDF(filePath);
      },
      onDownloadReport: (row) => {
        const filePath = row.reportUrl;
        if (!filePath) return;
        void handleDownloadPDF(filePath);
      },
      onRetry: (row) => {
        if (!row.taskUuid) return;
        void handleRetry(row);
      },
      canRerunSystem,
      downloadingFilePath,
      busy: Boolean(busyAction),
    }),
    [
      busyAction,
      canRerunSystem,
      downloadingFilePath,
      handleDownloadPDF,
      handlePreviewPDF,
      handleRetry,
    ],
  );

  return {
    actionHandlers,
    pdfDialogOpen,
    selectedPdfUrl,
    closePdfDialog: () => setPdfDialogOpen(false),
    isRetryBusy: Boolean(busyAction?.startsWith("retry:")),
  };
}
